import type { ResultOf } from 'gql.tada'
import type { ALL_COMBINED_INFRINGEMENTS_QUERY } from '../../queries'
import type { SeverityCategory } from '../../types'
import type { FollowUpActionLog } from '../infringement-follow-up'

export type CombinedInfringementRow = ResultOf<
  typeof ALL_COMBINED_INFRINGEMENTS_QUERY
>['allCombinedInfringements']['rows'][number]

export type CombinedInfringementSource = CombinedInfringementRow['source']

export const ALL_INFRINGEMENT_SOURCES: CombinedInfringementSource[] = [
  'DRIVING_REST',
  'WORKING_TIME',
  'TACHOGRAPH_USE',
]

export type CombinedRowStatus = 'OPEN' | 'STARTED' | 'CLOSED' | 'ARCHIVED'

export interface CombinedRowView {
  key: string
  source: CombinedInfringementSource
  id: string
  detailPathBase: string
  timestamp: string
  user: { id: string; fullName: string; phoneNumber: string | null }
  identifier: string
  text: string
  severity: SeverityCategory | null
  status: CombinedRowStatus
  wronglyGenerated: boolean
  /** Driving/rest rows show their own log entry; this is for the others. */
  latestActionLog: FollowUpActionLog | null
}

/**
 * What the combined list shows for one row, whichever infringement module it
 * comes from. The status is the backend's, the one its status filter uses.
 * `describe` renders a driving/rest category's description, which is
 * translated on the frontend; the other modules send their title already
 * translated.
 */
export function toCombinedRowView(
  row: CombinedInfringementRow,
  describe: (identifier: string, description: string) => string,
  drivingRestPathBase = '/infringements'
): CombinedRowView | null {
  if (row.source === 'DRIVING_REST' && row.infringement) {
    return {
      ...drivingRestRowView(row.infringement, describe, drivingRestPathBase),
      key: row.id,
      status: row.status,
    }
  }
  if (row.source === 'WORKING_TIME' && row.workInfringement) {
    const node = row.workInfringement
    return {
      key: row.id,
      source: row.source,
      id: node.id,
      detailPathBase: '/work-day-infringements',
      timestamp: node.infringementTimestamp,
      user: node.user,
      identifier: node.workInfringementCategory.identifier,
      text: node.workInfringementCategory.title,
      severity: null,
      status: row.status,
      wronglyGenerated: node.wronglyGenerated,
      latestActionLog: node.latestActionLog,
    }
  }
  if (row.source === 'TACHOGRAPH_USE' && row.tachographInfringement) {
    const node = row.tachographInfringement
    return {
      key: row.id,
      source: row.source,
      id: node.id,
      detailPathBase: '/tachograph-infringements',
      timestamp: node.infringementTimestamp,
      user: node.user,
      identifier: node.category.identifier,
      text: node.category.title,
      severity: node.category.severity as SeverityCategory,
      status: row.status,
      wronglyGenerated: node.wronglyGenerated,
      latestActionLog: node.latestActionLog,
    }
  }
  return null
}

interface DrivingRestNode {
  id: string
  infringementTimestamp: string
  status?: string | null
  user: { id: string; fullName: string; phoneNumber: string | null }
  infringementCategory: {
    identifier: string
    description: string
    severity: string
  }
}

/** A driving/rest infringement as a list row, also for lists that only hold
 * that type. */
export function drivingRestRowView(
  node: DrivingRestNode,
  describe: (identifier: string, description: string) => string,
  pathBase = '/infringements'
): CombinedRowView {
  return {
    key: node.id,
    source: 'DRIVING_REST',
    id: node.id,
    detailPathBase: pathBase,
    timestamp: node.infringementTimestamp,
    user: node.user,
    identifier: node.infringementCategory.identifier,
    text: describe(
      node.infringementCategory.identifier,
      node.infringementCategory.description
    ),
    severity: node.infringementCategory.severity as SeverityCategory,
    status: (node.status ?? 'OPEN') as CombinedRowStatus,
    wronglyGenerated: false,
    latestActionLog: null,
  }
}
